import React, { useState, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Switch,
  VStack,
  HStack,
  Divider,
  Heading,
  useToast,
  Input,
  FormHelperText,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { api } from '../services/api'

interface UserPreferences {
  editor_theme?: string
  editor_font_size?: number
  editor_font_family?: string
  editor_tab_size?: number
  editor_word_wrap?: boolean
  editor_minimap?: boolean
  editor_line_numbers?: boolean
  default_language?: string
  compiler_flags?: string
  ui_theme?: string
  show_hints?: boolean
  auto_save?: boolean
  notification_preferences?: Record<string, any>
  benchmark_visibility?: string
  submission_history_limit?: number
}

interface PreferencesPanelProps {
  isOpen: boolean
  onClose: () => void
}

const PreferencesPanel: React.FC<PreferencesPanelProps> = ({
  isOpen,
  onClose,
}) => {
  const [formData, setFormData] = useState<UserPreferences>({})
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data: preferences, isLoading } = useQuery<UserPreferences>(
    'userPreferences',
    () => api.get('/user/preferences').then(res => res.data),
    { enabled: isOpen }
  )

  useEffect(() => {
    if (preferences) {
      setFormData(preferences)
    }
  }, [preferences])

  const updatePreferencesMutation = useMutation(
    (data: UserPreferences) => api.put('/user/preferences', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('userPreferences')
        toast({
          title: 'Preferences updated',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        onClose()
      },
      onError: () => {
        toast({
          title: 'Update failed',
          description: 'Failed to update preferences. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      },
    }
  )

  const handleInputChange = (field: keyof UserPreferences, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    updatePreferencesMutation.mutate(formData)
  }

  const handleClose = () => {
    if (!updatePreferencesMutation.isLoading) {
      onClose()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <form onSubmit={handleSubmit}>
          <ModalHeader>Preferences</ModalHeader>
          <ModalCloseButton />
          
          <ModalBody pb={6} maxH="calc(90vh - 120px)" overflowY="auto">
            <VStack spacing={6} align="stretch">
              {/* Editor Preferences */}
              <VStack spacing={4} align="stretch">
                <Heading size="sm" color="gray.600">Editor Settings</Heading>
                
                <FormControl>
                  <FormLabel>Editor Theme</FormLabel>
                  <Select
                    value={formData.editor_theme || 'vs-dark'}
                    onChange={(e) => handleInputChange('editor_theme', e.target.value)}
                  >
                    <option value="vs-dark">Dark (Default)</option>
                    <option value="vs">Light</option>
                    <option value="hc-black">High Contrast Dark</option>
                    <option value="hc-light">High Contrast Light</option>
                  </Select>
                </FormControl>

                <HStack spacing={4}>
                  <FormControl>
                    <FormLabel>Font Size</FormLabel>
                    <NumberInput
                      value={formData.editor_font_size || 14}
                      onChange={(_, value) => handleInputChange('editor_font_size', value)}
                      min={10}
                      max={24}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Tab Size</FormLabel>
                    <NumberInput
                      value={formData.editor_tab_size || 4}
                      onChange={(_, value) => handleInputChange('editor_tab_size', value)}
                      min={2}
                      max={8}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </HStack>

                <FormControl>
                  <FormLabel>Font Family</FormLabel>
                  <Select
                    value={formData.editor_font_family || "'Monaco', 'Courier New', monospace"}
                    onChange={(e) => handleInputChange('editor_font_family', e.target.value)}
                  >
                    <option value="'Monaco', 'Courier New', monospace">Monaco (Default)</option>
                    <option value="'Fira Code', monospace">Fira Code</option>
                    <option value="'Source Code Pro', monospace">Source Code Pro</option>
                    <option value="'JetBrains Mono', monospace">JetBrains Mono</option>
                    <option value="'Consolas', monospace">Consolas</option>
                  </Select>
                </FormControl>

                <VStack spacing={3} align="stretch">
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" flex="1">
                      Word wrap
                    </FormLabel>
                    <Switch
                      isChecked={formData.editor_word_wrap || false}
                      onChange={(e) => handleInputChange('editor_word_wrap', e.target.checked)}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" flex="1">
                      Show minimap
                    </FormLabel>
                    <Switch
                      isChecked={formData.editor_minimap || false}
                      onChange={(e) => handleInputChange('editor_minimap', e.target.checked)}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" flex="1">
                      Show line numbers
                    </FormLabel>
                    <Switch
                      isChecked={formData.editor_line_numbers || false}
                      onChange={(e) => handleInputChange('editor_line_numbers', e.target.checked)}
                    />
                  </FormControl>
                </VStack>
              </VStack>

              <Divider />

              {/* Language & Compilation */}
              <VStack spacing={4} align="stretch">
                <Heading size="sm" color="gray.600">Language & Compilation</Heading>
                
                <FormControl>
                  <FormLabel>Default Language</FormLabel>
                  <Select
                    value={formData.default_language || 'cpp'}
                    onChange={(e) => handleInputChange('default_language', e.target.value)}
                  >
                    <option value="cpp">C++</option>
                    <option value="c">C</option>
                    <option value="python">Python</option>
                    <option value="javascript">JavaScript</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Default Compiler Flags</FormLabel>
                  <Input
                    value={formData.compiler_flags || ''}
                    onChange={(e) => handleInputChange('compiler_flags', e.target.value)}
                    placeholder="-O2 -g -fopencilk"
                  />
                  <FormHelperText>
                    Default compilation flags for C/C++ projects
                  </FormHelperText>
                </FormControl>
              </VStack>

              <Divider />

              {/* UI & Experience */}
              <VStack spacing={4} align="stretch">
                <Heading size="sm" color="gray.600">UI & Experience</Heading>
                
                <FormControl>
                  <FormLabel>UI Theme</FormLabel>
                  <Select
                    value={formData.ui_theme || 'light'}
                    onChange={(e) => handleInputChange('ui_theme', e.target.value)}
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="system">System Default</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Benchmark Visibility</FormLabel>
                  <Select
                    value={formData.benchmark_visibility || 'public'}
                    onChange={(e) => handleInputChange('benchmark_visibility', e.target.value)}
                  >
                    <option value="public">Public - Visible to others</option>
                    <option value="private">Private - Only visible to you</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Submission History Limit</FormLabel>
                  <NumberInput
                    value={formData.submission_history_limit || 50}
                    onChange={(_, value) => handleInputChange('submission_history_limit', value)}
                    min={10}
                    max={500}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <FormHelperText>
                    Maximum number of submissions to keep in history
                  </FormHelperText>
                </FormControl>

                <VStack spacing={3} align="stretch">
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" flex="1">
                      Show helpful hints
                    </FormLabel>
                    <Switch
                      isChecked={formData.show_hints || false}
                      onChange={(e) => handleInputChange('show_hints', e.target.checked)}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0" flex="1">
                      Auto-save code
                    </FormLabel>
                    <Switch
                      isChecked={formData.auto_save || false}
                      onChange={(e) => handleInputChange('auto_save', e.target.checked)}
                    />
                  </FormControl>
                </VStack>
              </VStack>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleClose} disabled={updatePreferencesMutation.isLoading}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              isLoading={updatePreferencesMutation.isLoading}
              loadingText="Saving..."
            >
              Save Preferences
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

export default PreferencesPanel 